<!--
  InAppBrowserEscape — the way out of another app's browser, shown BEFORE
  sign-in on the login and welcome screens (pack prompt 7 Part C, owner
  research 2026-11-03).

  Why before: a sign-in made inside Facebook's, Messenger's, Instagram's,
  TikTok's or WhatsApp's browser view stays in that view's cookie jar; the
  member comes back to the real browser signed out, and the app cannot be
  installed from there at all. So the escape comes first, and sign-in here is
  not blocked — the banner just stays.

  One button, picked by the platform: Android gets Chrome's intent link
  (Facebook and Messenger honour it, Instagram reportedly does not), iPhone
  gets `x-safari-https://` (works from many hosts, blocked by Instagram).
  Both fail silently, so the manual way — ⋯ then "open in browser" — is
  always written under it, and a copy-link is the last resort.

  Renders nothing outside an in-app view (capabilities.inAppKind), and never
  inside the Telegram Mini App (isTelegramEnv wins there). No overlay, no
  dismiss: depth 0, one decision.
-->
<template>
  <div v-if="kind" class="inapp-escape">
    <div class="inapp-escape__text">
      <strong class="inapp-escape__title">{{ title }}</strong>
      <span class="inapp-escape__body">{{ $t('inAppEscape.body') }}</span>
    </div>

    <!--
      `external` is load-bearing: F7's click handler would otherwise feed the
      intent:// or x-safari-https:// href to the router as a relative path.
    -->
    <a
      v-if="escapeUrl"
      class="external inapp-escape__action"
      :href="escapeUrl"
      :data-kind="kind"
    >
      <f7-icon
        :f7="android ? 'globe' : 'compass_fill'"
        class="inapp-escape__action-icon"
      ></f7-icon>
      <span>{{ $t(android ? 'inAppEscape.openChrome' : 'inAppEscape.openSafari') }}</span>
    </a>

    <!-- The ⋯ menu exists where the button does: a phone's in-app view. -->
    <p v-if="escapeUrl" class="inapp-escape__hint">{{ $t('inAppEscape.hint') }}</p>

    <button
      type="button"
      class="inapp-escape__copy"
      @click="copyLink"
    >
      <f7-icon f7="doc_on_doc" class="inapp-escape__copy-icon"></f7-icon>
      <span>{{ $t('inAppEscape.copyLink') }}</span>
    </button>
  </div>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { inAppKind, isAndroid, isAppleDevice } from '@/platform/capabilities.js';
import { chromeIntentUrlFor, currentPageUrl, safariUrlFor } from '@/utils/browserEscape.js';

const { t, te } = useI18n();

// Read once: the host app does not change under the page.
const kind = inAppKind();
const android = isAndroid();
const apple = isAppleDevice();

// Read once at setup. LoginPage strips the OIDC error parameters from the
// address only in its own mount, which runs AFTER this child's: currentPageUrl
// drops them itself, so the carried link is clean either way.
const pageUrl = currentPageUrl();

const escapeUrl = computed(() => {
  if (!pageUrl) return '';
  if (android) return chromeIntentUrlFor(pageUrl);
  if (apple) return safariUrlFor(pageUrl);
  return '';
});

const title = computed(() => {
  const nameKey = `inAppEscape.apps.${kind}`;
  if (kind && kind !== 'webview' && te(nameKey)) {
    return t('inAppEscape.title', { app: t(nameKey) });
  }
  return t('inAppEscape.titleUnknown');
});

function toast(key) {
  try {
    f7.toast.show({ text: t(key), position: 'center', closeTimeout: 2000 });
  } catch {
    /* no F7 app yet — the copy itself still happened or not */
  }
}

async function copyLink() {
  const url = pageUrl;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      toast('inAppEscape.copied');
      return;
    }
  } catch {
    /* fall through to the legacy path */
  }
  // The legacy command, where it exists; the helper never outlives the tap.
  let ok = false;
  if (typeof document.execCommand === 'function') {
    const area = document.createElement('textarea');
    try {
      area.value = url;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      area.setSelectionRange?.(0, url.length);
      ok = document.execCommand('copy') === true;
    } catch {
      ok = false;
    } finally {
      area.remove();
    }
  }
  toast(ok ? 'inAppEscape.copied' : 'inAppEscape.copyFailed');
}
</script>

<style scoped>
.inapp-escape {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 1rem;
  padding: 14px 14px 12px;
  border-radius: 14px;
  background: var(--poolit-warning-50, #fff7e6);
  border: 1px solid rgba(var(--poolit-warning-rgb, 245, 158, 11), 0.4);
  text-align: start;
}

.inapp-escape__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.inapp-escape__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--poolit-text-primary, #1a1a1a);
}

.inapp-escape__body {
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--poolit-text-secondary, #444444);
}

.inapp-escape__action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  border-radius: 12px;
  background: var(--poolit-primary-700, #4f46e5);
  color: #ffffff !important;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
}

.inapp-escape__action:active {
  transform: scale(0.98);
}

.inapp-escape__action-icon,
.inapp-escape__copy-icon {
  font-size: 18px;
}

.inapp-escape__hint {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--poolit-text-muted, #666666);
}

.inapp-escape__copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  align-self: center;
  min-height: 40px;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: var(--poolit-primary-700, #4f46e5);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
</style>
